import React, { useEffect, useState } from 'react';
import { Mail, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { settingsService, AppSettings } from '../services/settingsService';

const SettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await settingsService.getSettings();
      setSettings(data);
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEmail = async () => {
    if (!settings || saving) return;
    const newValue = !settings.emailEnabled;
    setSaving(true);
    try {
      await settingsService.updateSetting('emailEnabled', String(newValue));
      setSettings({ ...settings, emailEnabled: newValue });
      toast.success(`Email notifications ${newValue ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">App Settings</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">Manage global application configuration</p>
      </div>

      {!settings.emailEnabled && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            Email notifications are currently disabled. Survey invitations and reminders will not be sent.
          </p>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`p-2 rounded-lg ${
              settings.emailEnabled
                ? 'bg-green-100 dark:bg-green-900/30'
                : 'bg-red-100 dark:bg-red-900/30'
            }`}>
              <Mail className={`h-5 w-5 ${
                settings.emailEnabled
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">Email Notifications</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {settings.emailEnabled
                  ? 'Emails are being sent for survey invitations and reminders'
                  : 'No emails are being sent'}
              </p>
            </div>
          </div>

          <button
            onClick={handleToggleEmail}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
              settings.emailEnabled ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'
            }`}
            role="switch"
            aria-checked={settings.emailEnabled}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                settings.emailEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
